import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Check, ChevronRight, Utensils, X } from "lucide-react";
import { toast } from "sonner";

const SLOT_LABEL = { breakfast: "Breakfast", lunch: "Lunch", snack: "Snack", dinner: "Dinner" };

// Active saved meal plan. `compact` renders a slim strip (chat empty state) that
// links to the Tracker; full mode renders tickable meals that auto-log on tick.
export default function SavedPlanCard({ compact = false, onLogged }) {
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(-1);

  const load = () => api.get("/plans/active").then((r) => setPlan(r.data && r.data.id ? r.data : null)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!plan) return null;
  const meals = plan.meals || [];
  const done = meals.filter((m) => m.done).length;

  if (compact) {
    return (
      <Link to="/tracker" data-testid="active-plan-strip"
        className="mt-3 flex items-center gap-2 rounded-2xl border border-border bg-surface-elevated px-4 py-2.5 hover:border-secondary/40 transition">
        <Utensils size={15} className="text-secondary shrink-0" />
        <span className="text-sm text-foreground/80 flex-1 truncate">{plan.title} · <b className="text-primary">{done}/{meals.length}</b> meals done today</span>
        <ChevronRight size={16} className="text-muted-foreground shrink-0" />
      </Link>
    );
  }

  const toggle = async (i) => {
    setBusy(i);
    try {
      const { data } = await api.post(`/plans/${plan.id}/toggle-meal`, { index: i });
      setPlan(data);
      onLogged?.();
    } catch { toast.error("Couldn't update the plan."); }
    finally { setBusy(-1); }
  };

  const clearPlan = async () => {
    try {
      await api.delete("/plans/active");
      setPlan(null);
      toast.success("Plan cleared.");
    } catch { toast.error("Couldn't clear the plan."); }
  };

  return (
    <div className="bg-surface-elevated rounded-3xl border border-border p-4 sm:p-5 mb-4" data-testid="saved-plan-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="font-serif-display text-xl text-primary leading-tight" data-testid="saved-plan-title">{plan.title}</div>
          {plan.subtitle && <div className="text-xs text-muted-foreground mt-0.5">{plan.subtitle}</div>}
          {plan.tag && <span className="inline-block mt-1.5 text-[10px] font-semibold text-secondary bg-secondary/10 px-2 py-0.5 rounded-full">{plan.tag}</span>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs font-semibold text-secondary bg-secondary/10 px-2.5 py-1 rounded-full" data-testid="plan-progress">{done} / {meals.length} today ✓</span>
          <button onClick={clearPlan} data-testid="clear-plan-btn" title="Clear this plan"
            className="h-7 w-7 grid place-items-center rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition">
            <X size={15} />
          </button>
        </div>
      </div>
      <div className="divide-y divide-border">
        {meals.map((m, i) => (
          <button key={i} onClick={() => toggle(i)} disabled={busy === i}
            data-testid={`plan-meal-${i}`}
            className="w-full flex items-center gap-3 py-2.5 text-left disabled:opacity-60">
            <span className={`h-6 w-6 rounded-lg border-2 flex items-center justify-center shrink-0 transition-colors ${m.done ? "bg-secondary border-secondary text-white" : "border-border"}`}>
              {m.done && <Check size={14} strokeWidth={3} />}
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{SLOT_LABEL[m.slot] || m.slot}</span>
              <span className={`block text-sm truncate ${m.done ? "line-through text-muted-foreground" : "text-foreground"}`}>{m.dish}</span>
            </span>
            {m.kcal > 0 && <span className="text-xs text-muted-foreground whitespace-nowrap tabular-nums">~{Math.round(m.kcal)} kcal</span>}
          </button>
        ))}
      </div>
      <div className="text-[11px] text-muted-foreground mt-3 pt-3 border-t border-dashed border-border">Tick a meal and it logs to your tracker automatically.</div>
    </div>
  );
}
